import type { Draft, PayloadAction } from '@reduxjs/toolkit'
import { createAction, createSlice } from '@reduxjs/toolkit'
import type {
  DiscoverySummary,
  DraftState,
  PlannedOrderSettings,
  ArchiveTagCensus,
  PrimerDiscovery,
  PrimerEdit,
  PrimerEstimate,
  QuestionAnswer,
  SettingChange,
  SampleDiagnosis,
  SheetState,
  WizardFile,
  WizardItemType,
  WizardSampleGroup,
  WizardStep,
  SubmitChecks,
  SubmitFlow,
  SubmitReview,
  TargetCheck
} from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { IOrder } from '../../../Model/Order/IOrder.ts'
import type { SequencingPlatformKey } from '../../../Generated/SequencingPlatform.ts'
import { applyAnswer, refreshDerived } from '../../../Services/OrderWizard/questions.ts'
import type { ReopenableKind } from '../../../Services/OrderWizard/answers.ts'
import { reopenAnswer } from '../../../Services/OrderWizard/answers.ts'
import { controlDecision } from '../../../Services/OrderWizard/sampleLabels.ts'
import type { DetectedSequencingPlatform } from '../../../Util/sequencingPlatformDetection.ts'
import type { UnrestorableDraft } from '../../../Services/OrderWizard/draftStorage.ts'
import type { DropReason } from '../../../Services/OrderWizard/reconcile.ts'
import type { DropNotice } from '../../../Services/OrderWizard/dropNotice.ts'
import {
  releasedFileFor,
  removedGroupOf,
  restoredChoices,
  withGroupsReadded,
  withGroupsRemoved,
  withoutRemovedRecord,
  withRemovalsRetried
} from '../../../Services/OrderWizard/removal.ts'
import { withSettingChange } from '../../../Services/OrderWizard/orderSettingsPlan.ts'
import { planReverseJoins, withReverseJoins } from '../../../Services/OrderWizard/reverseFiles.ts'
import { expectsReverseFile } from '../../../Util/reverseFilePairing.ts'
import type { ColumnTarget } from '../../../Services/OrderWizard/sheet/sheetMapping.ts'
import { withColumnTargets } from '../../../Services/OrderWizard/sheet/sheetMapping.ts'
import { reviewSignature } from '../../../Services/OrderWizard/reviewSignature.ts'
import { DETECTION_FAILED_REJECTION } from '../../../Services/OrderWizard/dropPipeline.ts'
import type { ValidationIssue } from '../../../Model/Order/ValidationIssue.ts'

/**
 * State of the detection-driven order wizard. Keyed by draft id so several drafts can be prepared side by
 * side; `OrderSlice` holds exactly one current order and is deliberately not reused.
 */
export interface IOrderWizardState {
  drafts: Record<string, DraftState>
  activeDraftId: string | null
  /** Browser storage has been read once this session. */
  storageRestored: boolean
  /** Stored drafts that could not be read back (another schema version, corrupt); the classic view takes over. */
  unrestorable: UnrestorableDraft[]
  /** Restored drafts that comparing with their order dropped, until the lab has seen why. */
  dropped: DroppedDraft[]
  /** The confirmation of the last drop, until it is dismissed or times out. */
  dropNotice?: DropNotice
  /** The lab's unsubmitted orders no draft holds, offered for resuming on the start screen. */
  resumable?: ResumableOrder[]
  /** An order being turned into a draft, or why that failed. */
  resume?: { orderId: string; status: 'loading' | 'failed'; error?: string }
  /** The draft the lab discarded last, with what can still be done about its order. */
  discarded?: DiscardedDraft
  /** The lab discarded the open draft: the start screen comes next, never another draft opened for them. */
  startFresh?: true
}

export type DroppedDraft = { draftId: string; name: string; orderId?: string; reason: DropReason }

export type ResumableOrder = { orderId: string; name: string; createdAt?: string }

export type DiscardedDraft = {
  name: string
  orderId?: string
  deletion?: { status: 'deleting' | 'deleted' | 'failed'; error?: string }
}

export const initialOrderWizardState: IOrderWizardState = {
  drafts: {},
  activeDraftId: null,
  storageRestored: false,
  unrestorable: [],
  dropped: []
}

type DraftAction<P = object> = PayloadAction<P & { draftId: string }>

/** `null` takes the override back. */
type SubmitPlatform = SequencingPlatformKey | null

const MAX_CREATION_ATTEMPTS = 5

const withDraft = (state: Draft<IOrderWizardState>, draftId: string, update: (draft: Draft<DraftState>) => void) => {
  const draft = state.drafts[draftId]
  if (draft) {
    update(draft)
  }
}

/** Labels and the question queue follow the draft: recomputed after anything that can change them. */
const requeue = (draft: Draft<DraftState>) => {
  const refreshed = refreshDerived(draft as DraftState)
  draft.questions = refreshed.questions
  if (refreshed.groups !== draft.groups) {
    draft.groups = refreshed.groups
  }
  if (refreshed.settings !== draft.settings) {
    draft.settings = refreshed.settings
  }
}

/* an answer about a group removed meanwhile still names a server item its removal has to delete */
const recordForRemoval = (draft: Draft<DraftState>, groupKey: string, itemIds: string[]) => {
  const removal = draft.removals?.[groupKey]
  if (removal) {
    removal.itemIds = [...new Set([...removal.itemIds, ...itemIds])]
  }
}

const withFile = (draft: Draft<DraftState>, key: string, update: (file: Draft<WizardFile>) => void) => {
  const file = draft.files[key]
  if (file) {
    update(file)
  }
}

/* a file joining an item is uploaded, or taken back from the files the order kept */
const takeIntoUpload = (draft: Draft<DraftState>, key: string) =>
  withFile(draft, key, (file) => {
    file.rejection = undefined
    if (file.upload) {
      return
    }
    const serverFileId = releasedFileFor(draft.releasedFiles, file)
    if (serverFileId) {
      file.upload = { status: 'uploaded', progress: 1, serverFileId }
      draft.releasedFiles = draft.releasedFiles?.filter((entry) => entry.serverFileId !== serverFileId)
      if (draft.releasedFiles?.length === 0) {
        draft.releasedFiles = undefined
      }
    } else {
      file.upload = { status: 'pending', progress: 0 }
    }
  })

export const emptyDraft = (id: string, name: string, createdAt: string): DraftState => ({
  id,
  name,
  createdAt,
  phase: 'start',
  poolIds: [],
  files: {},
  fileOrder: [],
  groups: {},
  questions: [],
  restored: false
})

export const orderWizardSlice = createSlice({
  name: 'orderWizard',
  initialState: initialOrderWizardState,
  reducers: {
    draftCreated: (state, action: PayloadAction<{ draftId: string; name: string; createdAt: string }>) => {
      const { draftId, name, createdAt } = action.payload
      state.drafts[draftId] = emptyDraft(draftId, name, createdAt)
      state.activeDraftId = draftId
      state.startFresh = undefined
    },
    activeDraftSet: (state, action: PayloadAction<{ draftId: string | null }>) => {
      state.activeDraftId = action.payload.draftId
      state.startFresh = undefined
    },
    draftRenamed: (state, action: DraftAction<{ name: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (!draft.orderId) {
          draft.name = action.payload.name
        }
      })
    },
    /** Before the order exists; afterwards the description is edited through `orderDetailsSaveRequested`. */
    draftDescribed: (state, action: DraftAction<{ description: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (!draft.orderId) {
          draft.description = action.payload.description
        }
      })
    },
    /**
     * New files join the draft. A file it rejected before is judged afresh, since its partner may come
     * with it this time; any other known file is left to `filesReattached`.
     */
    filesAdded: (state, action: DraftAction<{ files: WizardFile[] }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const file of action.payload.files) {
          const known = draft.files[file.key]
          if (!known) {
            draft.fileOrder.push(file.key)
          }
          if (!known || known.rejection) {
            draft.files[file.key] = file
          }
        }
      })
    },
    /** Dropped again after a reload or a failure: the file has a handle again and can upload. */
    filesReattached: (state, action: DraftAction<{ keys: string[] }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const key of action.payload.keys) {
          withFile(draft, key, (file) => {
            if (file.upload && (file.upload.status === 'needs-readd' || file.upload.status === 'failed')) {
              file.upload = { status: 'pending', progress: 0 }
            }
          })
        }
      })
    },
    detectionStarted: (state, action: DraftAction) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (!draft.orderId) {
          draft.phase = 'detecting'
        }
        draft.error = undefined
      })
    },
    detectionCompleted: (
      state,
      action: DraftAction<{
        discovery?: DiscoverySummary
        settings?: PlannedOrderSettings
        platforms: Record<string, DetectedSequencingPlatform>
        rejections: Record<string, string>
        groups: WizardSampleGroup[]
        /** The 0-based pool the drop was made for; absent or 0 for the first. */
        pool?: number
        /** Dropped files that are the R2 of samples missing theirs. */
        attachments?: { groupKey: string; reverse: string }[]
      }>
    ) => {
      const { discovery, settings, platforms, rejections, groups, pool, attachments } = action.payload
      withDraft(state, action.payload.draftId, (draft) => {
        if (discovery) {
          draft.discovery = discovery
        }
        if (settings) {
          draft.settings = settings
        }
        for (const [key, platform] of Object.entries(platforms)) {
          withFile(draft, key, (file) => {
            file.platform = platform
          })
        }
        for (const [key, reason] of Object.entries(rejections)) {
          withFile(draft, key, (file) => {
            file.rejection = reason
            file.upload = undefined
          })
        }
        for (const group of groups) {
          const forward = draft.files[group.forward]
          const removed = forward && removedGroupOf(draft as DraftState, forward)
          const restored = removed ? restoredChoices(draft as DraftState, removed[1]) : {}
          draft.groups[group.key] = { ...group, ...restored, ...(pool ? { pool } : {}) }
          if (removed) {
            const { removedNames, removedGroups } = withoutRemovedRecord(draft as DraftState, removed[0])
            draft.removedNames = removedNames
            draft.removedGroups = removedGroups
          }
          for (const key of [group.forward, group.reverse]) {
            if (key) {
              takeIntoUpload(draft, key)
            }
          }
          const fileIds = [group.forward, group.reverse].flatMap((key) => (key && draft.files[key]?.upload?.serverFileId) || [])
          draft.removals = withRemovalsRetried(draft.removals, fileIds)
        }
        for (const { groupKey, reverse } of attachments ?? []) {
          const group = draft.groups[groupKey]
          if (!group || group.reverse || !draft.files[reverse]) {
            continue
          }
          group.reverse = reverse
          if (group.item.status === 'created') {
            group.reverseAttach = { status: 'pending' }
          }
          takeIntoUpload(draft, reverse)
        }
        if (pool && groups.length > 0) {
          draft.poolsAnswered = true
          draft.poolsSource = 'dropped'
        }
        /* "no controls" was said of the files before; named controls arriving later are labelled as on a first drop */
        if (draft.controlsAnswer === 'none' && groups.length > 0) {
          const { flagged } = controlDecision(draft as DraftState)
          if (groups.some((group) => flagged.has(group.key))) {
            draft.controlsAnswer = undefined
          }
        }
        if (!draft.orderId) {
          draft.phase = 'start'
        }
        requeue(draft)
      })
    },
    dropSettled: (state, action: PayloadAction<{ notice: DropNotice }>) => {
      state.dropNotice = action.payload.notice
    },
    dropNoticeDismissed: (state, action: PayloadAction<{ id: string }>) => {
      if (state.dropNotice?.id === action.payload.id) {
        state.dropNotice = undefined
      }
    },
    /** `keys`: the files the drop was reading, rejected so a drop or "Try again" reads them afresh. */
    detectionFailed: (state, action: DraftAction<{ error: string; keys?: string[] }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.phase = draft.orderId ? 'active' : 'error'
        draft.error = action.payload.error
        for (const key of action.payload.keys ?? []) {
          withFile(draft, key, (file) => {
            if (!draft.groups[key]) {
              file.rejection = DETECTION_FAILED_REJECTION
              file.upload = undefined
            }
          })
        }
      })
    },
    orderCreationRequested: (state, action: DraftAction) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.phase = 'creating'
        draft.error = undefined
      })
    },
    orderCreationAttempted: (state, action: DraftAction<{ at: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.creationAttempts = [...(draft.creationAttempts ?? []), action.payload.at].slice(-MAX_CREATION_ATTEMPTS)
      })
    },
    orderCreated: (state, action: DraftAction<{ orderId: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.orderId = action.payload.orderId
      })
    },
    poolCreated: (state, action: DraftAction<{ poolId: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (!draft.poolIds.includes(action.payload.poolId)) {
          draft.poolIds.push(action.payload.poolId)
        }
        draft.phase = 'active'
      })
    },
    orderCreationFailed: (state, action: DraftAction<{ error: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.phase = draft.orderId ? 'active' : 'error'
        draft.error = action.payload.error
      })
    },
    orderDetailsSaved: (state, action: DraftAction<{ name: string; description?: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.name = action.payload.name
        draft.description = action.payload.description
        draft.error = undefined
      })
    },
    orderDetailsSaveFailed: (state, action: DraftAction<{ error: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.error = action.payload.error
      })
    },
    /** Written into the order by the write queue; before the order exists the creation takes the settings. */
    settingChanged: (state, action: DraftAction<{ change: SettingChange }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (!draft.settings || draft.submission) {
          return
        }
        const { change } = action.payload
        const format = change.setting === 'sampleConfiguration' ? change.value : draft.settingsWrite?.sampleConfiguration
        draft.settings = withSettingChange(draft.settings as PlannedOrderSettings, change)
        draft.changedInClassic = draft.changedInClassic?.filter((setting) => setting !== change.setting)
        if (draft.changedInClassic?.length === 0) {
          draft.changedInClassic = undefined
        }
        draft.settingsWrite = {
          status: 'pending',
          revision: (draft.settingsWrite?.revision ?? 0) + 1,
          ...(format ? { sampleConfiguration: format } : {})
        }
        requeue(draft)
      })
    },
    targetChecked: (state, action: DraftAction<{ check?: TargetCheck }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.targetCheck = action.payload.check
      })
    },
    /** Once the order is paired-end, single-file samples whose names pair as R1 and R2 are joined. */
    settingsWritten: (state, action: DraftAction<{ revision: number }>) => {
      const current = state.drafts[action.payload.draftId]
      if (!current || current.settingsWrite?.revision !== action.payload.revision) {
        return
      }
      const format = current.settingsWrite.sampleConfiguration
      const written = { ...current, settingsWrite: undefined }
      state.drafts[action.payload.draftId] =
        format && expectsReverseFile(format) ? refreshDerived(withReverseJoins(written, planReverseJoins(written))) : written
    },
    /** In a paired-end order: samples missing their R2 take it from the samples and uploads their names pair with. */
    reversePairingRequested: (state, action: DraftAction) => {
      const current = state.drafts[action.payload.draftId]
      if (current && !current.submission && current.settings && expectsReverseFile(current.settings.sampleConfiguration.value)) {
        state.drafts[action.payload.draftId] = refreshDerived(withReverseJoins(current, planReverseJoins(current)))
      }
    },
    reverseFileAttached: (state, action: DraftAction<{ groupKey: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        const group = draft.groups[action.payload.groupKey]
        if (group) {
          group.reverseAttach = undefined
        }
      })
    },
    reverseFileAttachFailed: (state, action: DraftAction<{ groupKey: string; error: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        const group = draft.groups[action.payload.groupKey]
        if (group?.reverseAttach) {
          group.reverseAttach = { ...group.reverseAttach, status: 'failed', error: action.payload.error }
        }
      })
    },
    settingsWriteFailed: (state, action: DraftAction<{ revision: number; error: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (draft.settingsWrite?.revision === action.payload.revision) {
          draft.settingsWrite = { ...draft.settingsWrite, status: 'failed', error: action.payload.error }
        }
      })
    },
    settingsRetryRequested: (state, action: DraftAction) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (draft.settingsWrite?.status === 'failed') {
          const { revision, sampleConfiguration } = draft.settingsWrite
          draft.settingsWrite = { status: 'pending', revision, ...(sampleConfiguration ? { sampleConfiguration } : {}) }
        }
      })
    },
    /** `fromPaste`: a pasted block's rows, which keep the sheet they replace for restoring. */
    sheetLoaded: (state, action: DraftAction<{ sheet: SheetState; fromPaste?: boolean }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.replacedSheet = action.payload.fromPaste && draft.sheet ? draft.sheet : undefined
        draft.sheet = action.payload.sheet
        draft.sheetRef = undefined
        withFile(draft, action.payload.sheet.fileKey, (file) => {
          file.rejection = undefined
        })
        requeue(draft)
      })
    },
    /** What the registry says the sheet's primer columns name, for the columns they were read from. */
    sheetPrimersIdentified: (state, action: DraftAction<{ fileKey: string; registeredPrimers: SheetState['registeredPrimers'] }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (draft.sheet?.fileKey === action.payload.fileKey) {
          draft.sheet.registeredPrimers = action.payload.registeredPrimers
          requeue(draft)
        }
      })
    },
    /** The lab's column choices, as a new sheet revision: every item gets its row written again. */
    sheetMappingChanged: (state, action: DraftAction<{ targets: Record<string, ColumnTarget> }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (draft.sheet) {
          draft.sheet = withColumnTargets(draft.sheet as SheetState, action.payload.targets)
          requeue(draft)
        }
      })
    },
    sheetRestored: (state, action: DraftAction) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (draft.replacedSheet) {
          draft.sheet = draft.replacedSheet
          draft.replacedSheet = undefined
          requeue(draft)
        }
      })
    },
    replacedSheetDismissed: (state, action: DraftAction) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.replacedSheet = undefined
      })
    },
    /** The sheet leaves the draft; the next pass writes every item without it, clearing what it wrote. */
    sheetRemoved: (state, action: DraftAction) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.replacedSheet = undefined
        const sheetKeys = new Set(Object.values(draft.files).filter((file) => file.kind === 'sheet').map((file) => file.key))
        draft.sheet = undefined
        draft.sheetRef = undefined
        for (const key of sheetKeys) {
          delete draft.files[key]
        }
        draft.fileOrder = draft.fileOrder.filter((key) => !sheetKeys.has(key))
        requeue(draft)
      })
    },
    estimatesValidated: (state, action: DraftAction<{ keys: string[]; revision: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const key of action.payload.keys) {
          const group = draft.groups[key]
          if (group) {
            group.validatedFor = action.payload.revision
          }
        }
      })
    },
    itemTypeToggled: (state, action: DraftAction<{ groupKeys: string[]; itemType: 'SAMPLE' | 'NEGATIVE_CONTROL' }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        const groups = action.payload.groupKeys.map((key) => draft.groups[key]).filter((group) => !!group)
        for (const group of groups) {
          group.itemType = action.payload.itemType
          group.typeSource = 'user'
          group.item.error = undefined
        }
        if (groups.length > 0) {
          requeue(draft)
        }
      })
    },
    /** The groups and their files leave the draft; items the server may hold are left to the write queue. */
    itemsRemoved: (state, action: DraftAction<{ groupKeys: string[] }>) => {
      const current = state.drafts[action.payload.draftId]
      if (current && action.payload.groupKeys.some((key) => key in current.groups)) {
        state.drafts[action.payload.draftId] = refreshDerived(withGroupsRemoved(current, action.payload.groupKeys))
      }
    },
    /** Removed groups back from the files the order kept, with the lab's choices as they were. */
    itemsReadded: (state, action: DraftAction<{ groupKeys: string[] }>) => {
      const current = state.drafts[action.payload.draftId]
      if (current) {
        state.drafts[action.payload.draftId] = refreshDerived(withGroupsReadded(current, action.payload.groupKeys))
      }
    },
    removedGroupsDismissed: (state, action: DraftAction) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const removed of Object.values(draft.removedGroups ?? {})) {
          removed.dismissed = true
        }
      })
    },
    itemRemovalCompleted: (state, action: DraftAction<{ groupKey: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (draft.removals) {
          delete draft.removals[action.payload.groupKey]
          if (Object.keys(draft.removals).length === 0) {
            draft.removals = undefined
          }
        }
      })
    },
    itemRemovalFailed: (state, action: DraftAction<{ groupKeys: string[]; error: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const key of action.payload.groupKeys) {
          const removal = draft.removals?.[key]
          if (removal) {
            removal.status = 'failed'
            removal.error = action.payload.error
          }
        }
      })
    },
    removalRetryRequested: (state, action: DraftAction<{ groupKeys: string[] }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const key of action.payload.groupKeys) {
          const removal = draft.removals?.[key]
          if (removal?.status === 'failed') {
            removal.status = 'pending'
            removal.error = undefined
          }
        }
      })
    },
    itemConverted: (state, action: DraftAction<{ groupKey: string; itemType: 'SAMPLE' | 'NEGATIVE_CONTROL' }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        const group = draft.groups[action.payload.groupKey]
        if (group) {
          group.item.itemType = action.payload.itemType
          group.item.error = undefined
        }
      })
    },
    itemMoved: (state, action: DraftAction<{ groupKeys: string[]; poolId: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const key of action.payload.groupKeys) {
          const group = draft.groups[key]
          if (group) {
            group.item.poolId = action.payload.poolId
            group.item.error = undefined
          }
        }
      })
    },
    itemActionFailed: (state, action: DraftAction<{ groupKeys: string[]; error: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const key of action.payload.groupKeys) {
          const group = draft.groups[key]
          if (group) {
            group.item.error = action.payload.error
          }
        }
      })
    },
    stepChanged: (state, action: DraftAction<{ step: WizardStep }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.step = action.payload.step
      })
    },
    checksUpdated: (state, action: DraftAction<{ checks: SubmitChecks }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.checks = action.payload.checks
      })
    },
    runsRead: (state, action: DraftAction<{ runs: Record<string, string> }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const [key, run] of Object.entries(action.payload.runs)) {
          const group = draft.groups[key]
          if (group) {
            group.run = run
          }
        }
        requeue(draft)
      })
    },
    /** Clears a group's failed writes so the next pass makes them again. */
    groupRetryRequested: (state, action: DraftAction<{ groupKey: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        const group = draft.groups[action.payload.groupKey]
        if (!group) {
          return
        }
        group.item = group.item.status === 'failed' ? { status: 'waiting' } : { ...group.item, error: undefined }
        if (group.applied?.status === 'failed') {
          group.applied = undefined
        }
        if (group.reverseAttach?.status === 'failed') {
          group.reverseAttach = {
            status: 'pending',
            ...(group.reverseAttach.absorbItemId ? { absorbItemId: group.reverseAttach.absorbItemId } : {})
          }
        }
      })
    },
    warningsAcknowledged: (state, action: DraftAction<{ keys: string[] }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.acknowledged = [...new Set([...(draft.acknowledged ?? []), ...action.payload.keys])]
      })
    },
    poolRemoved: (state, action: DraftAction<{ poolId: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.poolIds = draft.poolIds.map((id) => (id === action.payload.poolId ? '' : id))
      })
    },
    poolRemovalRefused: (state, action: DraftAction<{ poolId: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.stuckPools = [...new Set([...(draft.stuckPools ?? []), action.payload.poolId])]
      })
    },
    poolLabelled: (state, action: DraftAction<{ poolId: string; label: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.poolLabels = { ...draft.poolLabels, [action.payload.poolId]: action.payload.label }
      })
    },
    poolCreatedAt: (state, action: DraftAction<{ pool: number; poolId: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.poolIds[action.payload.pool] = action.payload.poolId
      })
    },
    diagnosesAdded: (state, action: DraftAction<{ revision: string; diagnoses: { key: string; diagnosis: SampleDiagnosis }[] }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const { key, diagnosis } of action.payload.diagnoses) {
          const group = draft.groups[key]
          if (group) {
            group.diagnosis = diagnosis
            group.diagnosedFor = action.payload.revision
          }
        }
      })
    },
    draftStorageChanged: (state, action: DraftAction<{ kept: boolean }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.storageFailed = action.payload.kept ? undefined : true
      })
    },
    /** Review & submit: the write queue checks the order, reading only, once its pending writes are done. */
    reviewRequested: (state, action: DraftAction) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.submitFlow = { stage: 'checking', refusals: draft.submitFlow?.refusals }
      })
    },
    /** The check's findings for the draft as it was at `signature`: a review, or what the classic checks refused. */
    reviewLoaded: (
      state,
      action: DraftAction<{
        signature: string
        review?: SubmitReview
        refusal?: { reason: string; message: string }
        platformIssues: ValidationIssue[]
      }>
    ) => {
      withDraft(state, action.payload.draftId, (draft) => {
        const { signature, review, refusal, platformIssues } = action.payload
        draft.checks = { signature, ...(refusal ? { refusal } : {}), platformIssues }
        draft.submitFlow = { ...draft.submitFlow, signature, stage: 'checked', review, message: refusal?.message }
      })
    },
    reviewFailed: (state, action: DraftAction<{ message: string; signature?: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.submitFlow = {
          ...draft.submitFlow,
          signature: action.payload.signature ?? reviewSignature(draft),
          stage: 'failed',
          review: undefined,
          message: action.payload.message
        }
      })
    },
    platformOverrideRequested: (state, action: DraftAction<{ platform: SubmitPlatform }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.submitFlow = { stage: 'checked', ...draft.submitFlow, overrideInFlight: true }
      })
    },
    platformOverridden: (
      state,
      action: DraftAction<{ order: IOrder; previews: Pick<SubmitReview, 'preview' | 'platform' | 'creditCost'> }>
    ) => {
      withDraft(state, action.payload.draftId, (draft) => {
        const flow: SubmitFlow = { stage: 'checked', ...draft.submitFlow, overrideInFlight: false }
        const { order, previews } = action.payload
        draft.submitFlow = flow.review ? { ...flow, review: { ...flow.review, order, ...previews } } : flow
        if (flow.review && draft.checks && previews.platform) {
          draft.checks.platformIssues = previews.platform.issues ?? []
        }
      })
    },
    platformOverrideFailed: (state, action: DraftAction<{ error: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.submitFlow = { stage: 'checked', ...draft.submitFlow, overrideInFlight: false, issues: [action.payload.error] }
      })
    },
    submitRequested: (state, action: DraftAction) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (draft.submitFlow?.review) {
          draft.submitFlow.stage = 'submitting'
        }
      })
    },
    /** The order as it was at `signature` would not pass submit: the issue list says why. */
    submitRefused: (state, action: DraftAction<{ signature: string; checks: Omit<SubmitChecks, 'signature'> }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.checks = {
          ...draft.checks,
          platformIssues: draft.checks?.platformIssues,
          signature: action.payload.signature,
          ...action.payload.checks
        }
        draft.submitFlow = { ...draft.submitFlow, stage: 'checked', refusals: (draft.submitFlow?.refusals ?? 0) + 1 }
      })
    },
    orderSubmitted: (state, action: DraftAction<{ at: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        draft.submission = { status: 'submitted', at: action.payload.at }
      })
    },
    archiveTypeToggled: (state, action: DraftAction<{ groupKey: string; key: string; itemType: 'SAMPLE' | 'NEGATIVE_CONTROL' }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        const group = draft.groups[action.payload.groupKey]
        if (group) {
          group.archiveOverrides = { ...group.archiveOverrides, [action.payload.key]: action.payload.itemType }
          requeue(draft)
        }
      })
    },
    archiveItemsRecorded: (
      state,
      action: DraftAction<{ groupKey: string; items: Record<string, { itemId: string; itemType?: 'SAMPLE' | 'NEGATIVE_CONTROL' }> }>
    ) => {
      withDraft(state, action.payload.draftId, (draft) => {
        const group = draft.groups[action.payload.groupKey]
        if (group) {
          group.archiveItems = { ...group.archiveItems, ...action.payload.items }
        } else {
          recordForRemoval(
            draft,
            action.payload.groupKey,
            Object.values(action.payload.items).map((item) => item.itemId)
          )
        }
      })
    },
    archiveTagsAdded: (state, action: DraftAction<{ censuses: { key: string; census: ArchiveTagCensus }[] }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const { key, census } of action.payload.censuses) {
          const group = draft.groups[key]
          if (group) {
            group.archiveTags = census
          }
        }
      })
    },
    sheetFailed: (state, action: DraftAction<{ key: string; error: string }>) => {
      withDraft(state, action.payload.draftId, (draft) =>
        withFile(draft, action.payload.key, (file) => {
          file.rejection = action.payload.error
        })
      )
    },
    /** The pair read off samples whose R2 joined them, where their R1 alone showed one primer only. */
    primersRediscovered: (state, action: DraftAction<{ primers: PrimerDiscovery }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        if (draft.discovery?.primers.status === 'partial') {
          draft.discovery.primers = action.payload.primers
          requeue(draft)
        }
      })
    },
    estimatesAdded: (state, action: DraftAction<{ estimates: { key: string; estimate: PrimerEstimate }[] }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const { key, estimate } of action.payload.estimates) {
          const group = draft.groups[key]
          if (group) {
            group.estimate = estimate
          }
        }
        requeue(draft)
      })
    },
    sheetApplyStarted: (
      state,
      action: DraftAction<{
        groups: {
          key: string
          primerSource?: 'sheet' | 'manual' | 'estimate'
          sheetKey?: string
          primers?: string
          itemType?: WizardItemType
          fields?: string[]
        }[]
      }>
    ) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const { key, primerSource, sheetKey, primers, itemType, fields } of action.payload.groups) {
          const group = draft.groups[key]
          if (group) {
            group.applied = { status: 'pending', primerSource, sheetKey, primers, itemType, ...(fields ? { fields } : {}) }
            if (fields) {
              group.writtenFields = [...new Set([...(group.writtenFields ?? []), ...fields])]
            }
          }
        }
      })
    },
    /** Primers entered or pasted per side; `null` or blank takes the side's entry back, so the sheet or estimate shows again. */
    primersEdited: (state, action: DraftAction<{ edits: PrimerEdit[] }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const { key, ...sides } of action.payload.edits) {
          const group = draft.groups[key]
          if (!group) {
            continue
          }
          const next = { ...group.manualPrimers }
          for (const side of ['forward', 'reverse'] as const) {
            const value = sides[side]
            if (value === undefined) {
              continue
            }
            const cleaned = value?.trim().toUpperCase()
            if (cleaned) {
              next[side] = cleaned
            } else {
              delete next[side]
            }
          }
          if (next.forward === undefined && next.reverse === undefined) {
            delete group.manualPrimers
          } else {
            group.manualPrimers = next
          }
        }
        requeue(draft)
      })
    },
    sheetApplyFinished: (state, action: DraftAction<{ keys: string[]; error?: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        for (const key of action.payload.keys) {
          const group = draft.groups[key]
          const applied = group?.applied
          if (applied) {
            applied.status = action.payload.error ? 'failed' : 'done'
            applied.error = action.payload.error
            if (!action.payload.error && applied.fields) {
              group.writtenFields = applied.fields.length > 0 ? applied.fields : undefined
            }
          }
        }
      })
    },
    /** "Change" on an answer given before the order exists: the question is asked again. */
    answerReopened: (state, action: DraftAction<{ kind: ReopenableKind }>) => {
      const current = state.drafts[action.payload.draftId]
      if (current && !current.orderId && current.phase !== 'creating' && current.phase !== 'detecting') {
        state.drafts[action.payload.draftId] = refreshDerived({ ...reopenAnswer(current, action.payload.kind), error: undefined, phase: 'start' })
      }
    },
    questionAnswered: (state, action: DraftAction<{ answer: QuestionAnswer }>) => {
      const current = state.drafts[action.payload.draftId]
      if (current) {
        state.drafts[action.payload.draftId] = refreshDerived(applyAnswer(current, action.payload.answer))
      }
    },
    uploadStarted: (state, action: DraftAction<{ key: string }>) => {
      withDraft(state, action.payload.draftId, (draft) =>
        withFile(draft, action.payload.key, (file) => {
          file.upload = { status: 'uploading', progress: 0 }
        })
      )
    },
    uploadProgressed: (state, action: DraftAction<{ key: string; progress: number }>) => {
      withDraft(state, action.payload.draftId, (draft) =>
        withFile(draft, action.payload.key, (file) => {
          if (file.upload?.status === 'uploading') {
            file.upload.progress = action.payload.progress
            delete file.upload.notice
          }
        })
      )
    },
    uploadSucceeded: (state, action: DraftAction<{ key: string; serverFileId: string }>) => {
      withDraft(state, action.payload.draftId, (draft) =>
        withFile(draft, action.payload.key, (file) => {
          file.upload = { status: 'uploaded', progress: 1, serverFileId: action.payload.serverFileId }
        })
      )
    },
    uploadRetrying: (state, action: DraftAction<{ key: string; message: string }>) => {
      withDraft(state, action.payload.draftId, (draft) =>
        withFile(draft, action.payload.key, (file) => {
          file.upload = { status: 'uploading', progress: file.upload?.progress ?? 0, notice: action.payload.message }
        })
      )
    },
    uploadFailed: (state, action: DraftAction<{ key: string; error: string }>) => {
      withDraft(state, action.payload.draftId, (draft) =>
        withFile(draft, action.payload.key, (file) => {
          file.upload = { status: 'failed', progress: file.upload?.progress ?? 0, error: action.payload.error }
        })
      )
    },
    uploadRetryRequested: (state, action: DraftAction<{ key: string }>) => {
      withDraft(state, action.payload.draftId, (draft) =>
        withFile(draft, action.payload.key, (file) => {
          if (file.upload?.status === 'failed') {
            file.upload = { status: 'uploading', progress: 0 }
          }
        })
      )
    },
    uploadNeedsReadd: (state, action: DraftAction<{ key: string }>) => {
      withDraft(state, action.payload.draftId, (draft) =>
        withFile(draft, action.payload.key, (file) => {
          file.upload = { status: 'needs-readd', progress: 0 }
        })
      )
    },
    itemCreationStarted: (state, action: DraftAction<{ groupKey: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        const group = draft.groups[action.payload.groupKey]
        if (group) {
          group.item = { status: 'creating' }
        }
      })
    },
    itemCreated: (
      state,
      action: DraftAction<{ groupKey: string; itemId: string; itemType?: 'SAMPLE' | 'NEGATIVE_CONTROL'; poolId?: string }>
    ) => {
      withDraft(state, action.payload.draftId, (draft) => {
        const group = draft.groups[action.payload.groupKey]
        if (group) {
          group.item = {
            status: 'created',
            itemId: action.payload.itemId,
            itemType: action.payload.itemType,
            poolId: action.payload.poolId
          }
        } else {
          recordForRemoval(draft, action.payload.groupKey, [action.payload.itemId])
        }
      })
    },
    itemCreationFailed: (state, action: DraftAction<{ groupKey: string; error: string }>) => {
      withDraft(state, action.payload.draftId, (draft) => {
        const group = draft.groups[action.payload.groupKey]
        if (group) {
          group.item = { status: 'failed', error: action.payload.error }
        }
      })
    },
    draftsRestored: (state, action: PayloadAction<{ drafts: DraftState[]; unrestorable: UnrestorableDraft[] }>) => {
      for (const draft of action.payload.drafts) {
        state.drafts[draft.id] ??= refreshDerived(draft)
      }
      state.unrestorable = action.payload.unrestorable
      state.storageRestored = true
    },
    /** Replaces the draft with the version reconciled against the server, which wins every conflict. */
    draftReconciled: (state, action: PayloadAction<{ draft: DraftState }>) => {
      const { draft } = action.payload
      if (state.drafts[draft.id]) {
        state.drafts[draft.id] = refreshDerived(draft)
      }
    },
    /** Forgets the draft locally. The order, if one was created, stays and is reachable in the classic view. */
    draftDiscarded: (state, action: DraftAction<{ reason?: DropReason }>) => {
      const { draftId, reason } = action.payload
      const draft = state.drafts[draftId]
      if (draft && reason) {
        state.dropped.push({ draftId, name: draft.name, orderId: draft.orderId, reason })
      } else if (draft && !draft.submission) {
        state.discarded = { name: draft.name, ...(draft.orderId ? { orderId: draft.orderId } : {}) }
      }
      delete state.drafts[action.payload.draftId]
      if (state.activeDraftId === action.payload.draftId) {
        state.activeDraftId = null
        if (draft && !reason) {
          const pristine = Object.values(state.drafts).find((other) => !other.orderId && other.fileOrder.length === 0)
          if (pristine) {
            state.activeDraftId = pristine.id
          } else {
            state.startFresh = true
          }
        }
      }
    },
    droppedDraftsSeen: (state) => {
      state.dropped = []
    },
    resumableOrdersLoaded: (state, action: PayloadAction<{ orders: ResumableOrder[] }>) => {
      state.resumable = action.payload.orders
    },
    resumeRequested: (state, action: PayloadAction<{ orderId: string }>) => {
      state.resume = { orderId: action.payload.orderId, status: 'loading' }
    },
    /** A draft built from an unsubmitted order becomes the active one; whatever offered that order is settled. */
    draftResumed: (state, action: PayloadAction<{ draft: DraftState }>) => {
      const { draft } = action.payload
      const orderId = draft.orderId
      state.drafts[draft.id] = refreshDerived(draft)
      state.activeDraftId = draft.id
      state.resume = undefined
      state.resumable = state.resumable?.filter((entry) => entry.orderId !== orderId)
      state.unrestorable = state.unrestorable.filter((entry) => entry.orderId !== orderId)
      if (state.discarded?.orderId === orderId) {
        state.discarded = undefined
      }
    },
    resumeFailed: (state, action: PayloadAction<{ orderId: string; error: string }>) => {
      state.resume = { orderId: action.payload.orderId, status: 'failed', error: action.payload.error }
    },
    resumeNoticeDismissed: (state) => {
      state.resume = undefined
    },
    discardedOrderDeleteRequested: (state, action: PayloadAction<{ orderId: string }>) => {
      if (state.discarded?.orderId === action.payload.orderId) {
        state.discarded.deletion = { status: 'deleting' }
      }
    },
    discardedOrderDeleteFinished: (state, action: PayloadAction<{ orderId: string; error?: string }>) => {
      const { orderId, error } = action.payload
      if (state.discarded?.orderId === orderId) {
        state.discarded.deletion = error ? { status: 'failed', error } : { status: 'deleted' }
      }
      if (!error) {
        state.resumable = state.resumable?.filter((entry) => entry.orderId !== orderId)
      }
    },
    discardedNoticeDismissed: (state) => {
      state.discarded = undefined
    }
  }
})

/** Carries keys only, and the pool new FASTQ files go into; the `File` objects are attached to the file-handle registry by the view. */
const filesDropped = createAction<{ draftId: string; keys: string[]; pool?: number }>('orderWizard/filesDropped')
/** Starts every pending upload whose file handle is present. */
const uploadsRequested = createAction<{ draftId: string }>('orderWizard/uploadsRequested')
/** Edits the created order's title and description through the order update the classic view uses. */
const orderDetailsSaveRequested = createAction<{ draftId: string; name: string; description?: string }>(
  'orderWizard/orderDetailsSaveRequested'
)
const restoreRequested = createAction('orderWizard/restoreRequested')
const reconcileRequested = createAction<{ draftId: string }>('orderWizard/reconcileRequested')
/** The start screen asks for the lab's unsubmitted orders it could resume. */
const resumableOrdersRequested = createAction('orderWizard/resumableOrdersRequested')

export const orderWizardActions = {
  ...orderWizardSlice.actions,
  filesDropped,
  uploadsRequested,
  orderDetailsSaveRequested,
  restoreRequested,
  reconcileRequested,
  resumableOrdersRequested
}
